import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Card, 
  CardContent, 
  Grid, 
  Divider,
  Chip,
  Button,
  Alert
} from '@mui/material';
import { 
  Task as TaskIcon, 
  CheckCircle as CheckCircleIcon,
  EmojiEvents as AwardIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const MyEvaluations = () => {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [workloadData, setWorkloadData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [evalRes, workloadRes] = await Promise.allSettled([
          api.get('/evaluations/my-evaluations'),
          api.get('/workload-evaluations/my-tasks')
        ]);

        if (evalRes.status === 'fulfilled') {
          setEvaluations(evalRes.value.data || []);
        }
        if (workloadRes.status === 'fulfilled') {
          setWorkloadData(workloadRes.value.data);
        }
      } catch (err) {
        console.error('Failed to fetch evaluations', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isWorkloadEvaluated = Boolean(
    workloadData && (
      workloadData.is_evaluated ||
      workloadData.evaluation_status === 'ประเมินแล้ว' ||
      (workloadData.total_score !== null && workloadData.total_score !== undefined)
    )
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            ผลการประเมินการฝึกประสบการณ์ (My Evaluations)
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ระบบแสดงผลการประเมินสมรรถนะและผลการประเมินภาระงานตามรายวิชาจากอาจารย์นิเทศก์และครูพี่เลี้ยง
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<AwardIcon />}
          onClick={() => navigate('/dashboard/portfolio')}
          sx={{ borderRadius: 2 }}
        >
          ดูแฟ้มสะสมผลงาน (e-Portfolio)
        </Button>
      </Box>

      {/* 1. Workload Evaluation Card (คะแนนประเมินภาระงาน) */}
      {workloadData && (
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: 3, 
          borderLeft: '6px solid', 
          borderColor: isWorkloadEvaluated ? 'success.main' : 'warning.main',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <TaskIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    ผลการประเมินภาระงานตามรายวิชา (Workload Evaluation)
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {workloadData.course_code} {workloadData.course_name} | {workloadData.workload_form_title || 'แบบประเมินภาระงานประจำวิชา'}
                </Typography>
              </Box>

              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                {isWorkloadEvaluated ? (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="ได้รับการประเมินภาระงานแล้ว" 
                    color="success" 
                    sx={{ fontWeight: 'bold' }} 
                  />
                ) : (
                  <Chip 
                    label="รออาจารย์นิเทศก์ประเมินผล" 
                    color="warning" 
                    variant="outlined" 
                    sx={{ fontWeight: 'bold' }} 
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block' }}>
                    สถานะการประเมินภาระงาน:
                  </Typography>
                  <Box sx={{ my: 1 }}>
                    {isWorkloadEvaluated ? (
                      <Chip 
                        icon={<CheckCircleIcon />}
                        label="ประเมินแล้ว" 
                        color="success" 
                        sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, px: 1 }} 
                      />
                    ) : (
                      <Chip 
                        label="รอประเมิน" 
                        color="warning" 
                        variant="outlined" 
                        sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, px: 1 }} 
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {isWorkloadEvaluated 
                      ? 'อาจารย์นิเทศก์ประจำสาขาวิชาบันทึกผลการประเมินภาระงานเรียบร้อยแล้ว' 
                      : 'อยู่ระหว่างรออาจารย์นิเทศก์ประจำสาขาวิชาดำเนินการประเมิน'}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block' }}>
                    สถานะการส่งหลักฐานภาระงาน:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, my: 0.5, color: 'text.primary' }}>
                    ส่งหลักฐานแล้ว {workloadData.submissions?.length || 0} รายการ (จากทั้งหมด {workloadData.items?.length || 0} รายการ)
                  </Typography>
                  <Button 
                    size="small" 
                    variant="text" 
                    color="primary"
                    onClick={() => navigate('/dashboard/workload-submission')}
                    sx={{ p: 0, fontWeight: 600 }}
                  >
                    ไปที่หน้าส่งภาระงาน &rarr;
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {workloadData.feedback && (
              <Box sx={{ p: 2, bgcolor: '#fffbeb', borderRadius: 2, borderLeft: '4px solid #f59e0b' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#b45309', display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CommentIcon sx={{ fontSize: 16, mr: 0.5 }} /> ข้อเสนอแนะและข้อคิดเห็นของอาจารย์นิเทศก์:
                </Typography>
                <Typography variant="body2" sx={{ color: '#78350f', whiteSpace: 'pre-wrap' }}>
                  "{workloadData.feedback}"
                </Typography>
                {workloadData.evaluated_at && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#92400e' }}>
                    ประเมินเมื่อ: {new Date(workloadData.evaluated_at).toLocaleString('th-TH')}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* 2. Competency Evaluations (ผลการประเมินสมรรถนะ) */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        ผลการประเมินสมรรถนะ (Competency Evaluations)
      </Typography>

      {workloadData?.requires_supervision === 0 ? (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          รายวิชาที่ท่านลงทะเบียน ({workloadData?.course_code} {workloadData?.course_name}) ได้รับการกำหนดให้<strong>ยกเว้นการนิเทศ</strong> (ไม่ต้องรับการประเมินสมรรถนะจากอาจารย์นิเทศก์และครูพี่เลี้ยง)
        </Alert>
      ) : (
        <Grid container spacing={3}>
        {evaluations.map((evalItem: any) => (
          <Grid size={{ xs: 12 }} key={evalItem.id}>
            <Card sx={{ 
              borderLeft: '6px solid', 
              borderColor: evalItem.evaluator_role === 'PROFESSIONAL' 
                ? 'primary.main' 
                : (evalItem.evaluator_role === 'SPECIALIST' ? '#9c27b0' : 'success.main'), 
              boxShadow: 2,
              borderRadius: 2
            }}>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  mb: 2,
                  gap: 1
                }}>
                  <Box>
                    {evalItem.school_name && (
                      <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        โรงเรียน{evalItem.school_name}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        รอบการประเมิน:
                      </Typography>
                      <Chip 
                        label={
                          evalItem.evaluator_role === 'PROFESSIONAL' 
                            ? 'วิชาชีพครู (Professional)' 
                            : (evalItem.evaluator_role === 'SPECIALIST' ? 'วิชาเอก (Specialist)' : 'ครูพี่เลี้ยง (Mentor)')
                        } 
                        size="small" 
                        sx={{ fontSize: '0.8rem', fontWeight: 700 }} 
                        color={
                          evalItem.evaluator_role === 'PROFESSIONAL' 
                            ? 'primary' 
                            : (evalItem.evaluator_role === 'SPECIALIST' ? 'secondary' : 'success')
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, width: { xs: '100%', sm: 'auto' } }}>
                    <Chip 
                      label="ได้รับการนิเทศแล้ว" 
                      color="success" 
                      sx={{ fontWeight: 'bold', px: 1 }} 
                    />
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>ข้อเสนอแนะ:</Typography>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', bgcolor: 'grey.50', p: 2, borderRadius: 1, border: '1px dashed #ccc' }}>
                    "{evalItem.feedback || 'ไม่มีข้อเสนอแนะ'}"
                  </Typography>
                </Box>
                
                <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'right', color: 'text.secondary' }}>
                  ประเมินเมื่อ: {new Date(evalItem.created_at).toLocaleString('th-TH')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {evaluations.length === 0 && !loading && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="textSecondary">ยังไม่มีข้อมูลการประเมินสมรรถนะในขณะนี้ (อยู่ระหว่างรออาจารย์นิเทศก์หรือครูพี่เลี้ยงประเมิน)</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      )}
    </Box>
  );
};

export default MyEvaluations;
